import { ChangeDetectionStrategy, Component, Injector, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { ICountryModel } from '@fi-shared/fi-contacts/fi-countries-select/country';
import { BehaviorSubject } from 'rxjs';
import { isDefined } from '@fi-shared/utils';
import { FiCountryHelperService } from '@fi-shared/fi-contacts/fi-countries-select/country-helper.service';

@Component({
    selector: 'fi-login-name-input',
    template: `
        <mat-form-field
            appearance="outline"
            class="input-wrapper full-width"
            [hideRequiredMarker]="true"
            [ngClass]="{ 'phone-number-input': isCountryCodeMenuShown() }"
        >
            <mat-label>{{ 'auth.sign_in_email_or_phone_number' | fiAppText }}</mat-label>

            <div
                class="country-code-prefix"
                matPrefix
                *ngIf="isCountryCodeMenuShown() && currentCountry$ | async as currentCountry"
            >
                <fi-countries-menu
                    [countries]="countries$ | async"
                    [selectedCountry]="currentCountry"
                    (selectedCountryChange)="onSelectedCountryChange($event)"
                />

                <span class="country-code-label">({{ currentCountry?.CountryCode }})</span>
            </div>

            <input
                type="text"
                matInput
                [(ngModel)]="userLogin"
                (ngModelChange)="onUserLoginChange()"
                #loginName="ngModel"
                name="loginName"
                autocomplete="on"
                [required]="isRequired"
                [disabled]="isLoginDisabled"
            />

            <mat-error [fiErrorMessage]="loginName"></mat-error>
        </mat-form-field>
    `,
    styleUrls: ['./login-name-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-login-name-input' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiLoginNameInputComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiLoginNameInputComponent,
        },
    ],
    standalone: false,
})
export class FiLoginNameInputComponent extends ControlValueAccessorValidatorBase<string> implements OnInit {
    @Input() isLoginDisabled: boolean;
    @Input() isRequired: boolean = true;

    userLogin: string = '';
    countries$ = new BehaviorSubject<ICountryModel[]>([]);
    currentCountry$ = new BehaviorSubject<ICountryModel>(<ICountryModel>{});
    private tempCountryCode: string;

    constructor(
        private injector: Injector,
        private countryHelperService: FiCountryHelperService,
    ) {
        super(injector);
    }

    ngOnInit() {
        this.setUserLoginByNgModel();
        this.countryHelperService.getCountryByUserLocation().then((country: ICountryModel) => {
            this.updateCurrentCountry(country);
            this.setCountriesData();
        });
    }

    private setUserLoginByNgModel() {
        if (this.ngModel?.length && this.ngModel.indexOf('(+') === 0) {
            const phoneNumberStartIndex: number = this.ngModel.indexOf(')') + 1;
            this.userLogin = this.ngModel.slice(phoneNumberStartIndex);
            this.tempCountryCode = this.ngModel.slice(0, phoneNumberStartIndex).replace('(', '').replace(')', '');
        } else {
            this.userLogin = this.ngModel;
        }
    }

    private setCountriesData() {
        this.countryHelperService.getCountryCodes().then((data: ICountryModel[]) => {
            this.countries$.next(data);
            if (isDefined(this.tempCountryCode)) this.setCurrentCountryFromLogin();
        });
    }

    updateCurrentCountry(country: ICountryModel) {
        this.currentCountry$.next(country);
    }

    private setCurrentCountryFromLogin() {
        const savedLoginCountry: ICountryModel = this.countries$.value.find((country: ICountryModel) => {
            return country.CountryCode === this.tempCountryCode;
        });
        this.updateCurrentCountry(savedLoginCountry);
        this.tempCountryCode = undefined;
        return;
    }

    onSelectedCountryChange(newCountry: ICountryModel) {
        this.updateCurrentCountry(newCountry);
        this.countryHelperService.saveUserCountryModel(newCountry);
        this.onUserLoginChange();
    }

    onUserLoginChange() {
        if (this.isCountryCodeMenuShown()) {
            this.ngModel = !this.userLogin?.length
                ? ''
                : `(${this.currentCountry$.value.CountryCode}) ${this.userLogin}`;
        } else {
            this.ngModel = this.userLogin;
        }
        this.doPropagateChanges();
    }

    private readonly lettersRegExp: RegExp = /[a-zA-Z]/i;
    isCountryCodeMenuShown(): boolean {
        return !!this.userLogin?.length && !this.lettersRegExp.test(this.userLogin);
    }
}
